// components/product/ProductImageUploader.tsx
"use client";
import { ImagePlaceholderSVG } from "@/components/ui/SVG";
import { ProductClientState } from "@/zod/product.schema";
import Image from "next/image";
import { useFormContext } from "react-hook-form";

interface ProductImageUploaderProps {
  previewUrl?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPick?: () => void;
  id: string;
  label?: string;
  dimensions?: string;
  isLoading?: boolean;
  helperError?: string;
  helperSuccess?: string;
  mode?: "upload" | "pick";
}

export function ProductImageUploader({
  previewUrl,
  onChange,
  id,
  label = "Upload photo",
  dimensions = "dims: 372 x 256\nsize: less than 5MB",
  isLoading = false,
  helperError,
  helperSuccess,
  onPick,
  mode = "upload",
}: ProductImageUploaderProps) {
  const {
    getValues,
    formState: { errors },
  } = useFormContext<ProductClientState>();
  const currentImage = getValues("hero_image");
  const isPick = mode === "pick";
  return (
    <div className="relative w-full">
      {/* <div className="h-30 w-full"></div> */}
      <label
        htmlFor={id}
        className={`min-h-60 flex flex-col items-center justify-center text-gray-500 overflow-hidden relative ${
          isLoading ? "pointer-events-none opacity-60" : ""
        } ${isPick ? "cursor-pointer" : ""}`}
        aria-busy={isLoading}
        onClick={(e) => {
          if (isPick && onPick) {
            e.preventDefault();
            onPick();
          }
        }}
      >
        {previewUrl || currentImage ? (
          <Image
            src={previewUrl || currentImage || ""}
            alt="Preview"
            width={372}
            height={256}
          />
        ) : (
          <>
            <ImagePlaceholderSVG className="w-16 rounded-sm my-1" />
            <p>{label}</p>
            <span className="text-xs font-light text-center whitespace-pre-line">
              {dimensions}
            </span>
          </>
        )}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 z-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-slateCustom"></div>
          </div>
        )}
      </label>
      {!isPick ? (
        <input
          type="file"
          className="hidden"
          id={id}
          name={id}
          onChange={onChange}
          disabled={isLoading}
          aria-disabled={isLoading}
        />
      ) : null}
      {(errors.hero_image || helperError || helperSuccess) && (
        <div
          className={`text-xs ${helperError ? "text-red-600" : helperSuccess ? "text-green-600" : "text-red-600"}`}
          role={helperError ? "alert" : helperSuccess ? "status" : "alert"}
          aria-live="polite"
        >
          {helperError || helperSuccess || errors.hero_image?.message}
        </div>
      )}
    </div>
  );
}
